import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import React, { useEffect, useState } from 'react';
import {
  withInventoryCalculationParametersHoc,
  WithInventoryCalculationParametersHocChildProps,
} from '../../../api/graphql/hoc/withInventoryCalculationParametersHoc';
import {
  withUpdateInventoryCalculationParametersHoc,
  WithUpdateInventoryCalculationParametersHocChildProps,
} from '../../../api/graphql/hoc/withUpdateInventoryCalculationParametersHoc';
import { DialogSpinner } from '../../spinner';

const useStyles = makeStyles({
  dialogTitle: {
    fontWeight: 700,
  },
  sectionHeader: {
    fontWeight: 600,
    marginTop: 8,
    marginBottom: 4,
  },
  section: {
    marginTop: 6,
    marginBottom: 6,
  },
  divider: {
    marginTop: 8,
    marginBottom: 12,
  },
  formControl: {
    width: '100%',
  },
  infoPaper: {
    padding: 8,
    background: '#F7F7F9',
  },
});

export type InventoryCalculationSettingsDialogOwnProps = {
  open: boolean;
  grain_bin_id: number;
  onClose: () => void;
  onSaved?: () => void;
};

type Props = InventoryCalculationSettingsDialogOwnProps &
  WithInventoryCalculationParametersHocChildProps &
  WithUpdateInventoryCalculationParametersHocChildProps;

const InventoryCalculationSettingsDialog: React.FC<Props> = ({
  open,
  onClose,
  onSaved,
  grain_bin_id,
  inventory_calculation_parameters,
  refetchInventoryCalculationParameters,
  updateInventoryCalculationParameters,
  loading,
}) => {
  const [pfOption, setPfOption] = useState<string>('without');
  const [pfTestWeight, setPfTestWeight] = useState<number>(0);
  const [pfAdjIndex, setPfAdjIndex] = useState<string>('');
  const [pfValue, setPfValue] = useState<number>(0);
  const [volToBuOption, setVolToBuOption] = useState<string>('standard');
  const [buPerCuft, setBuPerCuft] = useState<number>(0.80356395);
  const [coneOption, setConeOption] = useState<string>('standard');
  const [coneAdjHeight, setConeAdjHeight] = useState<number>(0);
  const [volAdjustmentType, setVolAdjustmentType] = useState<string>('none');
  const [volAdjustmentCuft, setVolAdjustmentCuft] = useState<number>(0);
  const [densityOption, setDensityOption] = useState<string>('standard');
  const [grainDensity, setGrainDensity] = useState<number>(0);
  const [saving, setSaving] = useState<boolean>(false);
  const classes = useStyles();

  useEffect(() => {
    if (inventory_calculation_parameters) {
      setPfOption(inventory_calculation_parameters.pack_factor_calculation_option || 'none');
      setPfTestWeight(inventory_calculation_parameters.pack_factor_test_weight_lbsperbu || 0);
      setPfAdjIndex(inventory_calculation_parameters.pack_factor_adjustment_index || '');
      setPfValue(inventory_calculation_parameters.pack_factor_value || 0);
      setVolToBuOption(
        inventory_calculation_parameters.volume_to_bu_conversion_option || 'standard'
      );
      setBuPerCuft(inventory_calculation_parameters.bu_per_cuft_conversion_factor || 0.80356395);
      setConeOption(inventory_calculation_parameters.grain_volume_calculation_option || 'standard');
      setConeAdjHeight(inventory_calculation_parameters.equivalent_height_cone_adjustment || 0);
      setDensityOption(
        inventory_calculation_parameters.grain_density_calculation_option || 'standard'
      );
      setGrainDensity(inventory_calculation_parameters.grain_density_lbspercuft || 0);
    }
  }, [inventory_calculation_parameters]);

  const handleSave = async () => {
    try {
      setSaving(true);

      await updateInventoryCalculationParameters({
        grain_bin_id,
        pack_factor_calculation_option:
          pfOption === 'with_CUSTOM_TABLE_WITH_INDEX' || pfOption === 'with_CUSTOM_VALUE'
            ? 'with_CUSTOM'
            : pfOption,
        volume_to_bu_conversion_option: volToBuOption,
        grain_volume_calculation_option: coneOption,
        grain_density_calculation_option: densityOption,
        pack_factor_test_weight_lbsperbu: pfTestWeight || 0,
        pack_factor_adjustment_index: pfAdjIndex || '',
        pack_factor_value: pfValue || 0,
        bu_per_cuft_conversion_factor: buPerCuft || 0.80356395,
        equivalent_height_cone_adjustment: coneAdjHeight || 0,
        grain_density_lbspercuft: grainDensity || 0,
      });
      await refetchInventoryCalculationParameters();
      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      // no-op; follow existing pattern of silent error for now
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <DialogSpinner open title="Loading..." />;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className={classes.dialogTitle}>Inventory Calculation Settings</DialogTitle>
      <DialogContent>
        {/* Pack Factor Section */}
        <Typography variant="subtitle2" color="textSecondary" className={classes.sectionHeader}>
          Pack Factor Calculation
        </Typography>
        <Grid container spacing={2} className={classes.section}>
          <Grid item xs={12}>
            <FormControl variant="outlined" margin="dense" className={classes.formControl}>
              <InputLabel>Pack Factor Calculation Option</InputLabel>
              <Select
                value={pfOption}
                onChange={(e: any) => setPfOption(e.target.value)}
                label="Pack Factor Calculation Option"
              >
                <MenuItem value="without">No Pack Factor Compensation</MenuItem>
                <MenuItem value="with_USDA">USDA Pack Factor Table</MenuItem>
                <MenuItem value="with_CUSTOM">Custom Pack Factor Table</MenuItem>
                <MenuItem value="with_CUSTOM_TABLE_WITH_INDEX">
                  Custom Pack Factor Table with Adjustment Index
                </MenuItem>
                <MenuItem value="with_CUSTOM_VALUE">Custom Pack Factor Value</MenuItem>
              </Select>
              <FormHelperText>Select how pack factor should be determined</FormHelperText>
            </FormControl>
          </Grid>

          {pfOption !== 'none' && pfOption !== 'custom_value' && (
            <Grid item xs={12} sm={pfOption === 'custom_table_with_index' ? 6 : 12}>
              <TextField
                fullWidth
                variant="outlined"
                margin="dense"
                type="number"
                label="Nominal Test Weight"
                value={pfTestWeight}
                onChange={(e) =>
                  setPfTestWeight(
                    Number.isNaN(parseFloat(e.target.value)) ? 0 : parseFloat(e.target.value)
                  )
                }
                InputProps={{
                  endAdornment: <InputAdornment position="end">lbs/bu</InputAdornment>,
                }}
              />
            </Grid>
          )}

          {pfOption === 'custom_table_with_index' && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                variant="outlined"
                margin="dense"
                label="Adjustment Index"
                value={pfAdjIndex}
                onChange={(e) => setPfAdjIndex(e.target.value)}
              />
            </Grid>
          )}

          {pfOption !== 'none' && pfOption !== 'custom_value' && (
            <Grid item xs={12}>
              <Paper elevation={0} className={classes.infoPaper}>
                <Typography variant="body2">
                  Pack Factor: {pfValue ? pfValue.toFixed(3) : '—'}
                </Typography>
              </Paper>
            </Grid>
          )}

          {pfOption === 'custom_value' && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                variant="outlined"
                margin="dense"
                type="number"
                label="Pack Factor"
                value={pfValue}
                onChange={(e) =>
                  setPfValue(
                    Number.isNaN(parseFloat(e.target.value)) ? 0 : parseFloat(e.target.value)
                  )
                }
              />
            </Grid>
          )}
        </Grid>

        <Divider className={classes.divider} />

        {/* Volume to Bu. Section */}
        <Typography variant="subtitle2" color="textSecondary" className={classes.sectionHeader}>
          Volume to Bu. Conversion
        </Typography>
        <Grid container spacing={2} className={classes.section}>
          <Grid item xs={12} sm={6}>
            <FormControl variant="outlined" margin="dense" className={classes.formControl}>
              <InputLabel>Conversion Option</InputLabel>
              <Select
                value={volToBuOption}
                onChange={(e: any) => setVolToBuOption(e.target.value)}
                label="Conversion Option"
              >
                <MenuItem value="standard">Standard (0.80356395 bu. per cu.ft.)</MenuItem>
                <MenuItem value="custom">Custom bu. per cu.ft.</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {volToBuOption === 'custom' && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                variant="outlined"
                margin="dense"
                type="number"
                label="Bu. per cu.ft."
                value={buPerCuft}
                onChange={(e) =>
                  setBuPerCuft(
                    Number.isNaN(parseFloat(e.target.value)) ? 0 : parseFloat(e.target.value)
                  )
                }
              />
            </Grid>
          )}
        </Grid>

        <Divider className={classes.divider} />

        {/* Cone Volume Section */}
        <Typography variant="subtitle2" color="textSecondary" className={classes.sectionHeader}>
          Cone Volume Calculation
        </Typography>
        <Grid container spacing={2} className={classes.section}>
          <Grid item xs={12} sm={6}>
            <FormControl variant="outlined" margin="dense" className={classes.formControl}>
              <InputLabel>Calculation Option</InputLabel>
              <Select
                value={coneOption}
                onChange={(e: any) => setConeOption(e.target.value)}
                label="Calculation Option"
              >
                <MenuItem value="standard">Standard (actual estimated volume)</MenuItem>
                <MenuItem value="custom">Custom Cone Up/Down Adjustment</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {coneOption === 'custom' && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                variant="outlined"
                margin="dense"
                type="number"
                label="Cone Adjustment Height"
                value={coneAdjHeight}
                onChange={(e) =>
                  setConeAdjHeight(
                    Number.isNaN(parseFloat(e.target.value)) ? 0 : parseFloat(e.target.value)
                  )
                }
                InputProps={{ endAdornment: <InputAdornment position="end">ft</InputAdornment> }}
              />
            </Grid>
          )}
        </Grid>

        <Divider className={classes.divider} />

        {/* Grain Volume Adjustment Section */}
        <Typography variant="subtitle2" color="textSecondary" className={classes.sectionHeader}>
          Grain Volume Adjustment
        </Typography>
        <Grid container spacing={2} className={classes.section}>
          <Grid item xs={12} sm={6}>
            <FormControl variant="outlined" margin="dense" className={classes.formControl}>
              <InputLabel>Adjustment</InputLabel>
              <Select
                value={volAdjustmentType}
                onChange={(e: any) => setVolAdjustmentType(e.target.value)}
                label="Adjustment"
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="deduction">Deduction (Negative)</MenuItem>
                <MenuItem value="addition">Addition (Positive)</MenuItem>
              </Select>
              <FormHelperText>Apply a custom volume offset</FormHelperText>
            </FormControl>
          </Grid>
          {volAdjustmentType !== 'none' && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                variant="outlined"
                margin="dense"
                type="number"
                label="Volume Adjustment"
                value={volAdjustmentCuft}
                onChange={(e) =>
                  setVolAdjustmentCuft(
                    Number.isNaN(parseFloat(e.target.value)) ? 0 : parseFloat(e.target.value)
                  )
                }
                InputProps={{
                  endAdornment: <InputAdornment position="end">cu.ft.</InputAdornment>,
                }}
              />
            </Grid>
          )}
        </Grid>

        <Divider className={classes.divider} />

        {/* Grain Density Section */}
        <Typography variant="subtitle2" color="textSecondary" className={classes.sectionHeader}>
          Grain Density Calculation
        </Typography>
        <Grid container spacing={2} className={classes.section}>
          <Grid item xs={12} sm={6}>
            <FormControl variant="outlined" margin="dense" className={classes.formControl}>
              <InputLabel>Density Option</InputLabel>
              <Select
                value={densityOption}
                onChange={(e: any) => setDensityOption(e.target.value)}
                label="Density Option"
              >
                <MenuItem value="standard">Standard</MenuItem>
                <MenuItem value="custom">Custom Grain Density</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {densityOption === 'custom' && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                variant="outlined"
                margin="dense"
                type="number"
                label="Grain Density"
                value={grainDensity}
                onChange={(e) =>
                  setGrainDensity(
                    Number.isNaN(parseFloat(e.target.value)) ? 0 : parseFloat(e.target.value)
                  )
                }
                InputProps={{
                  endAdornment: <InputAdornment position="end">lbs/cu.ft.</InputAdornment>,
                }}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button color="primary" variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default withInventoryCalculationParametersHoc(
  withUpdateInventoryCalculationParametersHoc(InventoryCalculationSettingsDialog)
);
